import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import HarbourButton from './HarbourButton';
import HarbourIcon from './HarbourIcon';
import HarbourNotice from './HarbourNotice';
import { HarbourDateField, HarbourField } from './HarbourField';
import { apiAddReferralDocument } from '../../utils/rmsApi';
import {
  DATE_HINT,
  DOCUMENTS_UNTIL_SENT,
  ERROR_SUMMARY_BODY,
  ERROR_SUMMARY_TITLE,
  FILE_INPUT_ACCEPT,
  MAX_FILE_BYTES,
  REFERRAL_REFUSAL_MESSAGES,
  addFailure,
  capLine,
  documentTypeIds,
  fieldMessage,
  fileHint,
  readFileAsBase64,
  validateDocumentForm,
} from '../../utils/referralDocumentPortal';
import './harbour.css';
import './harbourDocuments.css';

/*
 * Add a document (Figma D-01 SENCO, D-05 P6, P7, P11 to P14 parent or carer
 * desktop, D-06 Q3 phone). One file, described in the uploader's own words,
 * typed from the organisation's grouped list and dated, all required before
 * the upload can finish (TDI-578, TDI-579). "Provided by" is never asked:
 * the server records it from the signed-in role.
 *
 * tone: 'senco' (plum Save) or 'family' (gold Save, the parent or carer's
 * one main action). layout: 'desktop' (form card with the notice in the
 * right rail, Cancel beside Save) or 'phone' (fields, the notice under
 * them, Save in the sticky bar; Back in the app bar is the way out).
 *
 * States: empty, filled in, errors (summary plus inline), saving (Save as
 * the loading button, the file field as uploading), could not add.
 */
const EMPTY_DATE = { day: '', month: '', year: '' };

function FileField({ file, error, hint, busy, onChoose }) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const inputRef = useRef(null);
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(' ');
  const open = () => { if (inputRef.current) inputRef.current.click(); };
  return (
    <div className="harbour-field harbour-file-field">
      <label htmlFor={inputId} className="harbour-field__label">Document</label>
      <p id={hintId} className="harbour-field__hint">{hint}</p>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={FILE_INPUT_ACCEPT}
        className="harbour-file-field__input"
        tabIndex={-1}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          const chosen = e.target.files && e.target.files[0];
          if (chosen) onChoose(chosen);
          // Let the same file be chosen again after a Change.
          e.target.value = '';
        }}
      />
      {file ? (
        <div className="harbour-file-field__box" aria-live="polite">
          <span className="harbour-file-field__mark">
            <HarbourIcon name={busy ? 'spinner' : 'file-pdf'} size={24} />
          </span>
          <span className="harbour-file-field__name">{file.name}</span>
          {busy ? (
            <span className="sr-only">Uploading</span>
          ) : (
            <button
              type="button"
              className="harbour-text-link harbour-file-field__change"
              aria-describedby={describedBy}
              onClick={open}
            >
              Change
              <span className="sr-only"> the file</span>
            </button>
          )}
        </div>
      ) : (
        <span>
          <HarbourButton kind="secondary" aria-describedby={describedBy} onClick={open}>
            Choose a file
          </HarbourButton>
        </span>
      )}
      {error && (
        <p id={errorId} className="harbour-field__error">
          <HarbourIcon name="error" size={16} />
          {error}
        </p>
      )}
    </div>
  );
}

export default function HarbourDocumentForm({
  orgId,
  patientRecordId,
  summary,
  tone = 'senco',
  layout = 'desktop',
  onAdded,
  onCancel,
}) {
  const maxBytes = (summary && summary.maxFileBytes) || MAX_FILE_BYTES;
  const groups = useMemo(() => (summary && summary.documentTypeGroups) || [], [summary]);
  const typeIds = useMemo(() => documentTypeIds(groups), [groups]);

  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [date, setDate] = useState(EMPTY_DATE);
  const [errors, setErrors] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(false);
  // Counts failed tries, so focus returns to the summary on every one.
  const [attempt, setAttempt] = useState(0);
  const summaryRef = useRef(null);
  const failureRef = useRef(null);

  // The summary or the failure takes focus when it appears, so a keyboard
  // or screen reader user hears what went wrong (GOV.UK error summary).
  useEffect(() => { if (showSummary && summaryRef.current) summaryRef.current.focus(); }, [showSummary, attempt]);
  useEffect(() => { if (failure && failureRef.current) failureRef.current.focus(); }, [failure]);

  const message = (field) => (errors[field] ? fieldMessage(errors[field], maxBytes) : null);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    setFailure(null);
    const checked = validateDocumentForm({ file, description, documentType, date }, { maxBytes, typeIds });
    setErrors(checked.errors);
    if (Object.keys(checked.errors).length > 0) {
      setShowSummary(true);
      setAttempt((n) => n + 1);
      return;
    }
    setShowSummary(false);
    setBusy(true);
    try {
      const fileBase64 = await readFileAsBase64(file);
      const res = await apiAddReferralDocument({
        orgId,
        patientRecordId,
        fileName: file.name,
        fileBase64,
        description: description.trim(),
        documentType,
        dateCovered: checked.dateCovered,
      });
      setBusy(false);
      if (onAdded) onAdded((res && res.document) || { description: description.trim() });
    } catch (err) {
      setBusy(false);
      const outcome = addFailure(err, maxBytes);
      if (outcome.field) {
        setErrors({ [outcome.field]: outcome.reason });
        setShowSummary(true);
        setAttempt((n) => n + 1);
      } else {
        setFailure(outcome.notice);
      }
    }
  };

  // Nothing to build the form from yet: the type list, the cap and whether
  // adding is still open all come with the summary. Opened straight from a
  // link, the page renders once before its loader has even started.
  if (!summary) {
    return (
      <div className={`harbour-doc-form harbour-doc-form--${layout}`} aria-busy="true">
        <p className="sr-only">Loading</p>
        <div aria-hidden="true" className="flex flex-col gap-5 max-w-[760px]">
          <div className="harbour-skeleton h-[108px] animate-pulse" />
          <div className="harbour-skeleton h-[80px] animate-pulse" />
        </div>
      </div>
    );
  }

  // Adding has closed (the referral was sent, or the cap reached) since this
  // screen was opened: say so instead of offering a form that will be refused.
  if (!summary.canAdd || summary.sent) {
    const reason = summary.sent ? 'referral_sent' : summary.addRefusal;
    return (
      <div className={`harbour-doc-form harbour-doc-form--${layout}`}>
        <HarbourNotice kind="info" title="You can’t add a document now">
          {REFERRAL_REFUSAL_MESSAGES[reason] || REFERRAL_REFUSAL_MESSAGES.not_an_uploader}
        </HarbourNotice>
        {onCancel && (
          <span>
            <HarbourButton kind="secondary" onClick={onCancel}>Back</HarbourButton>
          </span>
        )}
      </div>
    );
  }

  const aside = (
    <HarbourNotice kind="info" className="harbour-doc-form__aside-notice">
      {[capLine(summary), DOCUMENTS_UNTIL_SENT].filter(Boolean).map((line) => <p key={line}>{line}</p>)}
    </HarbourNotice>
  );
  const saveKind = tone === 'family' ? 'family' : 'primary';

  const fields = (
    <div className="harbour-doc-form__fields">
      {showSummary && Object.keys(errors).length > 0 && (
        <div ref={summaryRef} tabIndex={-1} className="harbour-doc-form__summary">
          <HarbourNotice kind="error" title={ERROR_SUMMARY_TITLE}>{ERROR_SUMMARY_BODY}</HarbourNotice>
        </div>
      )}
      {failure && (
        <div ref={failureRef} tabIndex={-1} className="harbour-doc-form__summary">
          <HarbourNotice kind="error" title={failure.title}>{failure.body}</HarbourNotice>
        </div>
      )}
      <FileField
        file={file}
        busy={busy}
        hint={fileHint(maxBytes)}
        error={message('file')}
        onChoose={(chosen) => {
          setFile(chosen);
          setFailure(null);
          setErrors((prev) => {
            const next = { ...prev };
            delete next.file;
            return next;
          });
        }}
      />
      <HarbourField
        label="Describe what it is"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        error={message('description')}
        disabled={busy}
      />
      <HarbourField
        as="select"
        label="Document type"
        value={documentType}
        onChange={(e) => setDocumentType(e.target.value)}
        error={message('documentType')}
        className="harbour-doc-form__type"
        disabled={busy}
      >
        <option value="" />
        {groups.map((g) => (
          <optgroup key={g.id} label={g.label}>
            {(g.types || []).map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </optgroup>
        ))}
      </HarbourField>
      <HarbourDateField
        label="Date"
        hint={DATE_HINT}
        value={date}
        onChange={setDate}
        error={message('date')}
      />
    </div>
  );

  if (layout === 'phone') {
    return (
      <form className="harbour-doc-form harbour-doc-form--phone" noValidate onSubmit={submit}>
        {fields}
        {aside}
        <div className="harbour-doc-form__bar">
          <HarbourButton
            type="submit"
            kind={saveKind}
            className="harbour-doc-form__bar-cta"
            busy={busy}
            busyLabel="Saving"
            disabled={busy}
            aria-busy={busy}
          >
            Save
          </HarbourButton>
        </div>
      </form>
    );
  }

  return (
    <div className="harbour-task-body harbour-doc-form harbour-doc-form--desktop">
      <div className="harbour-task-body__steps" aria-hidden="true" />
      <form className="harbour-task-body__main harbour-form-card" noValidate onSubmit={submit}>
        {fields}
        <div className="harbour-form-footer">
          {onCancel && (
            <HarbourButton kind="tertiary" onClick={onCancel} disabled={busy}>Cancel</HarbourButton>
          )}
          <HarbourButton
            type="submit"
            kind={saveKind}
            busy={busy}
            busyLabel="Saving"
            disabled={busy}
            aria-busy={busy}
          >
            Save
          </HarbourButton>
        </div>
      </form>
      <div className="harbour-task-body__aside">{aside}</div>
    </div>
  );
}
